import type { BadgeTone } from '@/components/admin/entretiens/status-badge';
import type {
  DecisionOrigin,
  ObjectiveStatus,
  SessionRefusalCode,
  SessionStatus,
  SessionVariableKind,
  StepCapReached,
  TurnOutcomeKind,
  TurnSeat,
} from '@matter_extractor/shared';
import { EXPERIENCE_NOTE_STARS, SESSION_TTL_HOURS } from '@matter_extractor/shared';
import { MISSING_DURATION, durationLabel } from './duration-label';

/**
 * The vocabulary of the session (§5.3, §9.5, §12.1), PURE and in one place.
 *
 * The régime of `lib/link-labels.ts` and `lib/versioned-labels.ts`: a screen
 * never words a refusal from the server's French sentence - that is a
 * developer's string - it words it from the `code`. And the dates are written
 * BY HAND rather than through `Intl`, because `Intl` answers according to the
 * machine's locale and a test and a capture would then not say the same thing
 * on two runners.
 */

/* ------------------------------------------------------------- the statuses */

/** The five statuses of §12.1 and #199, in French, for the operator's list. */
const STATUS_LABEL: Readonly<Record<SessionStatus, string>> = {
  'in-progress': 'en cours',
  finished: 'terminée',
  closed: 'fermée',
  interrupted: 'interrompue',
  expired: 'expirée',
};

export function sessionStatusLabel(status: SessionStatus): string {
  return STATUS_LABEL[status];
}

/**
 * The tone of the pill, on the SAME six-tone register the interviews use
 * (`admin/entretiens/status-badge.tsx`).
 *
 * Reused and never re-invented: a state must read the same way on both screens,
 * and a second palette would be a second meaning for the same colour. Info for
 * a conversation under way, success for one that reached its last page, neutral
 * for the three that stopped - nothing here is an irreversible action, so nothing
 * is `danger`.
 */
export function sessionStatusTone(status: SessionStatus): BadgeTone {
  if (status === 'in-progress') return 'info';
  if (status === 'finished') return 'success';
  return 'neutral';
}

/* -------------------------------------------------------------- the refusals */

/**
 * Each refusal of the session, worded from its `code` and never from the
 * message beside it. `null` on a code this screen has nothing to say about -
 * the caller then falls back to its own sentence.
 */
export function sessionRefusalSentence(code: string | null): string | null {
  switch (code) {
    case 'entry-unknown':
      return 'Ce lien n’ouvre pas cette expérience.';
    case 'entry-unresolved':
      return 'Cette expérience n’est plus jouable. Demandez un nouveau lien à la personne qui vous a invité.';
    case 'engine-missing':
      return 'Cette expérience n’est pas encore ouverte. Demandez un nouveau lien à la personne qui vous a invité.';
    case 'session-over':
      return 'Cette conversation est terminée.';
    case 'page-mismatch':
      return 'Cette page n’est plus celle de la conversation. Rechargez-la pour reprendre où vous en étiez.';
    case 'values-refused':
      return 'Complétez les champs obligatoires pour continuer.';
    case 'turn-in-flight':
      // Le moteur joue déjà un tour (§9.5). Ce n'est pas une erreur de la
      // personne : c'est la réponse précédente qui n'a pas fini d'arriver, donc
      // la phrase dit d'attendre plutôt que de recommencer.
      return 'Votre message précédent est encore en cours. Attendez la réponse avant d’envoyer le suivant.';
    case 'choice-refused':
      // Une page de choix se quitte par l'une de ses deux cartes (noyau.md
      // §5.1) : le bouton est grisé tant que rien n'est sélectionné, donc ce
      // refus ne se lit que sur un onglet resté ouvert sur une vieille page.
      return 'Choisissez l’un des deux textes pour continuer.';
    default:
      return null;
  }
}

/** Whether a refusal of the start means « ce lien ne mène nulle part » (§5.1). */
export function isDeadEntryRefusal(code: string | null): code is SessionRefusalCode {
  return code === 'entry-unresolved' || code === 'engine-missing' || code === 'entry-unknown';
}

/* ---------------------------------------------------------------- the dates */

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const;

function parts(iso: string): { date: Date; valid: boolean } {
  const date = new Date(iso);
  return { date, valid: !Number.isNaN(date.getTime()) };
}

/** `14:32`, the local clock, written by hand. */
export function sessionTime(iso: string): string {
  const { date, valid } = parts(iso);
  if (!valid) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** `3 septembre à 14:32`, or `aujourd’hui à 14:32` when it is today. */
export function sessionMoment(iso: string, now = new Date()): string {
  const { date, valid } = parts(iso);
  if (!valid) return '';
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  const month = MONTHS[date.getMonth()] ?? '';
  const day = sameDay ? 'aujourd’hui' : `le ${date.getDate()} ${month}`;
  return `${day} à ${sessionTime(iso)}`;
}

const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avril',
  'mai',
  'juin',
  'juill.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

/**
 * `12 oct. 10:02` - a moment as the sources of the blank analyst name it
 * (relecture.md §11). Never « aujourd’hui »: the label of a source is read again
 * days later, in the list of a session's trials, and must still say the same.
 */
export function sessionShortMoment(iso: string): string {
  const { date, valid } = parts(iso);
  if (!valid) return '';
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()] ?? ''} ${sessionTime(iso)}`;
}

/** `3 septembre 2026, 14:32` - the column of §12.1. */
export function sessionDate(iso: string): string {
  const { date, valid } = parts(iso);
  if (!valid) return '';
  return `${date.getDate()} ${MONTHS[date.getMonth()] ?? ''} ${date.getFullYear()}, ${sessionTime(iso)}`;
}

/* ------------------------------------------------------------ the endings */

/**
 * What the reprise page of §5.3 says about a session this browser remembers.
 *
 * Three shapes and not four: `finished` and `closed` and `expired` all end in
 * « Recommencer » alone, and the mockup (`PageReprendre`) gives the two ended
 * ones one heading. What separates them is the sentence, because a person who
 * finished and a person whose 24 hours ran out did not have the same evening.
 */
export function resumeHeading(status: SessionStatus): string {
  switch (status) {
    case 'in-progress':
      return 'Vous avez une conversation en cours';
    case 'finished':
      return 'Vous avez terminé ce parcours';
    case 'closed':
    case 'interrupted':
      return 'Cette conversation s’est arrêtée';
    default:
      return 'Cette conversation a expiré';
  }
}

export function resumeSentence(status: SessionStatus, startedAt: string): string {
  switch (status) {
    case 'in-progress':
      return `Commencée ${sessionMoment(startedAt)}. Vous pouvez la reprendre là où vous l’aviez laissée, ou en recommencer une.`;
    case 'finished':
      return 'Merci. Vous pouvez recommencer une conversation si vous le souhaitez.';
    case 'closed':
    case 'interrupted':
      return 'Vous pouvez recommencer une conversation si vous le souhaitez.';
    default:
      return `Une conversation se termine automatiquement ${SESSION_TTL_HOURS} heures après son début. Vous pouvez en recommencer une.`;
  }
}

/** The sober, fixed screen of §5.3 - shown inside the experience, not at the door. */
export const EXPIRED_HEADING = 'Cette conversation est terminée';
export const EXPIRED_SENTENCE = `Elle s’est terminée automatiquement ${SESSION_TTL_HOURS} heures après son début. Merci du temps que vous y avez passé.`;
/**
 * The same screen for a session an operator INTERRUPTED (issue #199, interruption.md §6):
 * the same title, and a sentence of its own - nothing of the engine's excuse, nothing
 * of the 24 hours.
 */
export const INTERRUPTED_SENTENCE = 'Elle a été arrêtée. Merci du temps que vous y avez passé.';

/* ---------------------------------------------------------- the variables */

// What a token renders as on a page shown to the PERSON is `resolvePersonVariable`
// of `shared` since the grill Variables of 2026-09-16 (D4, H62): total, and
// fed the matter and the criterion directions the session view carries.

/**
 * One value the person wrote, as the SCREENS print it (§12.2, §12.3).
 *
 * A boolean is a CHECKBOX, and the grill words it: « une case cochée se lit
 * "coché" et jamais `true` ». The unchecked case is worded too rather than left
 * blank - a page that recorded « non coché » said something, and an empty cell
 * would read as a value nobody captured. An empty string is the third case and
 * it is not a hole either: the person pressed the button with the field
 * cleared, which is a fact about an optional field, so it is named.
 *
 * Here and not beside one of the two screens, because BOTH print it - the card
 * of the parcours and the État column of the conversation - and two copies of
 * one rule is how « décoché » came to stand beside « non coché » for the same
 * boolean. The type is the union both stores carry (`SessionNamedValue` of the
 * walk, `SessionVariable` of the snapshot), which is the same union.
 */
export function namedValueLabel(value: string | boolean): string {
  if (typeof value === 'boolean') return value ? 'coché' : 'non coché';
  return value.trim() === '' ? 'laissé vide' : value;
}

/**
 * A Note's stored value as the SCREENS print it (retour-experience.md §4.2, §8,
 * issue #176): « 4 / 5 », in text and never in stars - Sessions draws no
 * widget, and the stored integer stays the one reading of the answer.
 *
 * A value that is not an integer of the scale - a blank, a row a hand edited -
 * is worded as any other value would be, so the cell says what is stored
 * rather than an invented « 0 / 5 ».
 */
export function noteValueLabel(value: string): string {
  const stars = Number(value.trim());
  const onScale = Number.isInteger(stars) && stars >= 1 && stars <= EXPERIENCE_NOTE_STARS;
  return onScale ? `${stars} / ${EXPERIENCE_NOTE_STARS}` : namedValueLabel(value);
}

/**
 * `namedValueLabel`, told the NATURE of the variable when the screen knows it:
 * the walk's values carry a block id and a name and no kind, the inventory
 * carries the kind (`SessionInventoryEntry`), and the card finds one from the
 * other by the key. Only a Note changes the wording, so every other kind - and
 * a value whose kind is unknown - reads exactly as before.
 */
export function kindedValueLabel(
  value: string | boolean,
  kind: SessionVariableKind | undefined,
): string {
  if (kind === 'note' && typeof value === 'string') return noteValueLabel(value);
  return namedValueLabel(value);
}

/* ------------------------------------------------- the figures of §12.1-12.3 */

/**
 * A duration in MILLISECONDS, which is what `durationMs` of a row carries.
 *
 * It delegates to `durationLabel` rather than writing a second formula - that
 * module exists to be adopted, and says so - and adds the one thing it does not
 * cover: an hour. A session runs up to 24 hours (§5.3), and « 154 min » is a
 * figure a reader has to divide in their head.
 */
export function sessionDurationLabel(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return MISSING_DURATION;
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return durationLabel(minutes);
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${String(minutes % 60).padStart(2, '0')}`;
}

/** The same, for a duration nobody could measure - a protocol of one turn. */
export function sessionDurationOrNothing(ms: number | null): string {
  return ms === null ? MISSING_DURATION : sessionDurationLabel(ms);
}

/**
 * Euros, with the comma France writes and the two decimals a bill has.
 *
 * `Intl` is deliberately not used here, at the régime the dates of this file
 * already follow: it answers according to the MACHINE's locale, so a test and a
 * capture would not print the same thing on two runners.
 */
export function costLabel(eur: number): string {
  if (!Number.isFinite(eur)) return MISSING_DURATION;
  return `${eur.toFixed(2).replace('.', ',')} €`;
}

/** Three decimals, for one model call, where two would round most of them to zero. */
export function callCostLabel(eur: number): string {
  if (!Number.isFinite(eur)) return MISSING_DURATION;
  return `${eur.toFixed(3).replace('.', ',')} €`;
}

/**
 * A token count, thinned to thousands above ten thousand.
 *
 * « 84 k » rather than « 84 130 » in a tile: the tile answers « is this session
 * expensive », a question three significant digits already over-answer. The
 * table of the Appels layer prints the exact figure, because that one answers
 * « which seat spent it ».
 */
export function tokensLabel(count: number): string {
  if (!Number.isFinite(count)) return MISSING_DURATION;
  if (count < 10_000) return String(count);
  return `${Math.round(count / 1000)} k`;
}

/** An exact count with the thin space France groups thousands with. */
export function exactCount(count: number): string {
  return String(Math.round(count)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
}

/* ------------------------------------------------- the vocabulary of a turn */

/**
 * What the engine decided, in French (§8.3, §8.4).
 *
 * The six outcomes of `turnOutcomeSchema`, worded as the SPEC words them so an
 * operator reading the screen and an agent reading §8.4 use one vocabulary.
 */
export const TURN_OUTCOME_LABEL: Record<TurnOutcomeKind, string> = {
  continue: 'continuer',
  'close-step': 'clôturer l’étape',
  'replay-step': 'relancer l’étape',
  'go-to-step': 'aller à une étape',
  'close-protocol': 'clôturer le protocole',
  'close-experience': 'fermer l’expérience',
};

export function turnOutcomeLabel(kind: TurnOutcomeKind): string {
  return TURN_OUTCOME_LABEL[kind];
}

/**
 * WHO produced a decision (§8.7) - the half of a turn that answers « pourquoi ».
 *
 * `implicit` has no identifier and the phrase says why rather than leaving a
 * blank: the implicit logic is not written anywhere, it is what the engine does
 * when nothing was written (§8.3).
 */
export function decisionOriginLabel(origin: DecisionOrigin): string {
  switch (origin.kind) {
    case 'guardrail':
      return 'garde-fou';
    case 'condition':
      return 'condition de sortie';
    default:
      return 'logique implicite';
  }
}

/**
 * WHICH ceiling of §8.3 the engine decided on (issue #97).
 *
 * The first two are the words the protocol editor already uses for the same two
 * things - `COUNTER_TRIGGER_LABEL` of `protocol-labels.ts` - stripped of the
 * verb that makes a trigger an EVENT: « plafond de tours atteint » and « quota
 * de réponses vides dépassé » become the two states, under a label that already
 * says « atteint ». They are not imported from there for exactly that reason.
 *
 * The third is NOT the « Budget IA par étape » of Réglages, and the difference
 * is the possessive: Réglages names the SETTING, one figure for every step of
 * every session, where this line names the ceiling THIS step was played
 * against. « l'étape » is what makes the sentence about the step the reader has
 * under their eyes rather than about a number somebody typed elsewhere.
 *
 * All three are worded to follow « au », which is what `protocolClosingSentence`
 * hangs them on: « au plafond de tours », « au quota de réponses vides », « au
 * budget IA de l'étape ».
 */
const STEP_CAP_LABEL: Record<NonNullable<StepCapReached>, string> = {
  turns: 'plafond de tours',
  'empty-answers': 'quota de réponses vides',
  budget: 'budget IA de l’étape',
};

export function stepCapLabel(cap: StepCapReached): string | null {
  return cap === null ? null : STEP_CAP_LABEL[cap];
}

/**
 * An objective's status as the screens ink it: the three of §7.3, with `réussi`
 * split in two since issue #112.
 *
 * `passable` is a réussi the END of its step validated at the passable bar -
 * every criterion at least passable, the quantity reached. It is the engine's
 * `succeeded` all the same, and counts as one wherever a success is counted
 * (the parcours, the protocol's logic); only the ink tells it apart, in yellow,
 * the same yellow a passable score is written in. The mark is read off the
 * snapshot and never recomputed (§8.7).
 */
export type ObjectiveTone = ObjectiveStatus | 'passable';

export function objectiveTone(state: {
  readonly status: ObjectiveStatus;
  readonly passableOnly?: boolean | undefined;
}): ObjectiveTone {
  return state.status === 'succeeded' && state.passableOnly === true ? 'passable' : state.status;
}

/**
 * The three statuses of an objective in session (§7.3), and the passable réussi.
 *
 * « réussi (passable) » and not « passable » alone: the word has to say that it
 * IS a success - it closed the step as one - and the parenthesis says how.
 */
const OBJECTIVE_STATUS_LABEL: Record<ObjectiveTone, string> = {
  succeeded: 'réussi',
  passable: 'réussi (passable)',
  failed: 'raté',
  empty: 'vide',
};

export function objectiveStatusLabel(tone: ObjectiveTone): string {
  return OBJECTIVE_STATUS_LABEL[tone];
}

/** Its tone, on the register the two other screens already use. */
export function objectiveStatusTone(tone: ObjectiveTone): BadgeTone {
  if (tone === 'succeeded') return 'success';
  if (tone === 'passable') return 'warning';
  if (tone === 'failed') return 'danger';
  return 'neutral';
}

/** Which seat made a call (§8.1). Two per turn, and they are not interchangeable. */
export function turnSeatLabel(seat: TurnSeat): string {
  return seat === 'interviewer' ? 'Interviewer' : 'Scribe';
}

/*
  `thresholdLabel` lived here until issue #81 and is now in
  `lib/protocol-labels.ts`: a threshold is a PROTOCOL's word - the objective
  poses it - and the editor of §7.2 reads the same sentence as these three
  screens do.
*/
